import styled from '@emotion/styled';
import { Typography } from '@material-ui/core';
import { ClassAttributes, createElement } from 'react';
import { FormattedMessage } from 'react-intl';

import { Project } from '../../../generated/graphql';
import { isDemo } from '../../../graphql/project';
import _DemoBadge from '../../component/primitive/DemoBadge';
import DummyContent from '../../component/primitive/DummyContent';
import ProjectStateBadge from '../../component/primitive/ProjectStateBadge';
import { SPACING_UNIT } from '../../constant/spacing';
import { gridLayoutContainer, gridLayoutItem } from '../../styleUtility/gridLayout';

interface Props {
  project: (Pick<Project, 'id' | 'title' | 'finalized' | 'state'> & { createdBy: { name: string } }) | null;
  className?: string;
}

const Header = ({ project, className }: ClassAttributes<HTMLElement> & Props) => {
  const projectName = project ? (
    <ProjectName variant="h5" color="primary">
      {project.title} {isDemo(project) ? <DemoBadge>DEMO</DemoBadge> : null}
    </ProjectName>
  ) : (
    <DummyProjectName size="h5" />
  );

  const projectState = project ? (
    <ProjectState variant="body2">
      <ProjectStateBadge projectState={project.finalized ? 'finalized' : project.state} />
    </ProjectState>
  ) : (
    <DummyProjectState size="body2" />
  );

  const projectCreatedByName = project ? (
    <ProjectCreatedByName variant="body2" color="textSecondary">
      <FormattedMessage
        id="show_project_page.header.project_created_by_name"
        values={{ name: project.createdBy!.name }}
      />
    </ProjectCreatedByName>
  ) : (
    <DummyProjectCreatedByName size="body2" />
  );

  return (
    <Root className={className}>
      {projectName}

      {projectState}

      {projectCreatedByName}
    </Root>
  );
};

const Root = styled('div')(
  gridLayoutContainer({
    columns: ['auto', '1fr'],
    rows: ['auto', 'auto'],
    areas: [
      ['projectName', 'projectName'],
      ['projectState', 'projectCreatedBy'],
    ],
  }),
);

const projectNameStyle = { ...gridLayoutItem('projectName'), marginBottom: SPACING_UNIT };

const ProjectName = styled(Typography)({
  ...projectNameStyle,
  wordBreak: 'break-word',
});

const DemoBadge = styled(_DemoBadge)({
  verticalAlign: 'text-bottom',
  marginLeft: 8,
});

const DummyProjectName = styled(DummyContent)({ ...projectNameStyle });

const projectStateStyle = [gridLayoutItem('projectState'), { marginRight: SPACING_UNIT * 2 }];

const ProjectState = styled(Typography)(projectStateStyle);

const DummyProjectState = styled(DummyContent)(projectStateStyle);

const projectCreatedByNameStyle = gridLayoutItem('projectCreatedBy', { alignSelf: 'center' });

const ProjectCreatedByName = styled(Typography)({
  ...projectCreatedByNameStyle,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const DummyProjectCreatedByName = styled(DummyContent)(projectCreatedByNameStyle);

export default Header;
